<script setup lang="ts">
import { ref, onMounted } from 'vue';
import type { OptionList } from '@Types';
import { useDataTableFilters } from '@DataTableModule';

const emits = defineEmits<{
  onParamsUpdate: [];
}>();

const props = withDefaults(
  defineProps<{
    defaultValue: string;
    hasClearTrigger?: boolean;
    inputLabel: string;
    isUrl?: boolean;
    loading?: boolean;
    optionLabel?: string;
    optionValue?: string;
    options?: OptionList[];
    paramsLabel: string;
  }>(),
  {
    defaultValue: '',
    optionLabel: 'label',
    optionValue: 'value',
  }
);

const { filterUrl, selectFilter } = useDataTableFilters();

const _model = ref<string>();
const showClear = ref(false);

onMounted(() => initValue());

function initValue() {
  const hasSelection = _model.value;
  if (hasSelection) {
    // Set showClear based on whether existing value is default
    if (props.hasClearTrigger) {
      showClear.value = _model.value !== props.defaultValue;
    }
    return;
  }

  if (props.isUrl) {
    filterUrl.value = filterUrl.value ? filterUrl.value : props.defaultValue;
    _model.value = filterUrl.value;
    // Set showClear based on whether value is default
    if (props.hasClearTrigger) {
      showClear.value = _model.value !== props.defaultValue;
    }
  }

  const params = new URLSearchParams(selectFilter.value || '');

  const selectFilterValue = {
    label: props.paramsLabel,
    value: params.get(props.paramsLabel) ?? props.defaultValue,
  };

  params.set(props.paramsLabel, params.get(props.paramsLabel) ?? props.defaultValue);

  selectFilter.value = `&${params.toString()}`;

  _model.value = selectFilterValue.value;

  // Set showClear based on whether value is default
  if (props.hasClearTrigger) {
    showClear.value = _model.value !== props.defaultValue;
  }
}

function checkSelectType() {
  // If model is null/undefined (cleared), set it back to defaultValue
  if (!_model.value) {
    _model.value = props.defaultValue;
  }

  const targetValue = _model.value;

  // Show clear button only if hasClearTrigger is true AND value is not the default
  if (props.hasClearTrigger) {
    showClear.value = targetValue !== props.defaultValue;
  }

  if (props.isUrl) {
    filterUrl.value = targetValue;
    emits('onParamsUpdate');
    return;
  }

  const existingParams = new URLSearchParams(selectFilter.value?.replace('&', '') || '');
  existingParams.set(props.paramsLabel, targetValue);
  selectFilter.value = `&${existingParams.toString()}`;
  emits('onParamsUpdate');
}
</script>

<template>
  <Dropdown
    v-model="_model"
    class="flex h-9 w-fit gap-3"
    inputId="selectFilter"
    pt:emptymessage="text-sm"
    pt:input="flex items-center p-1 pl-3 pr-6 text-sm text-gray-500"
    pt:item="text-sm"
    :optionLabel
    :optionValue
    :options
    :showClear
    :disabled="loading"
    :placeholder="$t(inputLabel)"
    @change="checkSelectType"
  />
</template>
